'use client';

import { Check, Loader2 } from 'lucide-react';
import { makeSvgIdsUnique } from '../utils/svg';

export interface ChatScreenMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  imageUrl?: string | null;
  modelName?: string | null;
  modelIconSvg?: string | null;
}

interface ChatScreenProps {
  messages: ChatScreenMessage[];
  isStreaming?: boolean;
  streamingContent?: string;
  streamingModel?: {
    name: string;
    iconSvg: string | null;
  } | null;
  copiedId?: string | null;
  userPhotoUrl?: string | null;
  barHeight?: number;
  messagesContainerRef: React.RefObject<HTMLDivElement | null>;
  messagesEndRef: React.RefObject<HTMLDivElement | null>;
  onMessagePressStart: (id: string, content: string) => void;
  onMessagePressEnd: () => void;
}

export function ChatScreen({
  messages,
  isStreaming = false,
  streamingContent = '',
  streamingModel = null,
  copiedId = null,
  userPhotoUrl,
  barHeight = 80,
  messagesContainerRef,
  messagesEndRef,
  onMessagePressStart,
  onMessagePressEnd,
}: ChatScreenProps) {
  return (
    <div className="relative flex min-h-0 flex-1 overflow-hidden bg-background">
      <div className="absolute inset-x-0 top-0 z-20 flex h-[50px] items-center border-b border-white/5 bg-background/80 px-4 backdrop-blur-lg">
        <div className="min-w-0 flex-1">
          <h1 className="truncate font-benzin text-base font-semibold text-white">Чат</h1>
        </div>
      </div>

      <div
        ref={messagesContainerRef}
        className="absolute inset-x-0 bottom-0 top-[50px] overflow-y-auto px-4 py-4"
      >
        <div className="space-y-6">
          {messages.map((message) => (
            <div
              key={message.id}
              className="relative flex select-none gap-3"
              onTouchStart={() => onMessagePressStart(message.id, message.content)}
              onTouchEnd={onMessagePressEnd}
              onTouchCancel={onMessagePressEnd}
              onContextMenu={(event) => event.preventDefault()}
            >
              {copiedId === message.id && (
                <div className="absolute -top-6 left-1/2 z-10 flex -translate-x-1/2 items-center gap-1 rounded-full bg-primary/90 px-2 py-0.5 text-xs font-medium text-black">
                  <Check className="h-3 w-3" />
                  Скопировано
                </div>
              )}

              {message.role === 'user' ? (
                userPhotoUrl ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={userPhotoUrl}
                    alt=""
                    className="h-[25px] w-[25px] flex-shrink-0 rounded-full object-cover"
                  />
                ) : (
                  <div className="flex h-[25px] w-[25px] flex-shrink-0 items-center justify-center rounded-full bg-primary/20 text-[10px] font-bold text-primary">
                    Вы
                  </div>
                )
              ) : (
                <ModelAvatar iconSvg={message.modelIconSvg} />
              )}

              <div className="min-w-0 flex-1">
                <p className="mb-0.5 text-[16px] font-semibold leading-[1.5] text-white">
                  {message.role === 'user' ? 'Вы' : message.modelName || 'AI'}
                </p>
                {message.imageUrl && (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={message.imageUrl}
                    alt=""
                    className="mb-2 max-w-[200px] rounded-xl"
                  />
                )}
                <div className="whitespace-pre-wrap break-words text-[16px] font-light leading-[1.56] tracking-[-0.4px] text-[#e7e8ea]">
                  {message.content}
                </div>
              </div>
            </div>
          ))}

          {isStreaming && (
            <div className="flex gap-3">
              <ModelAvatar iconSvg={streamingModel?.iconSvg} />
              <div className="min-w-0 flex-1">
                <p className="mb-0.5 text-[16px] font-semibold leading-[1.5] text-white">
                  {streamingModel?.name || 'AI'}
                </p>
                <div className="whitespace-pre-wrap break-words text-[16px] font-light leading-[1.56] tracking-[-0.4px] text-[#e7e8ea]">
                  {streamingContent || (
                    <span className="flex items-center gap-2 text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Думаю...
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}

          <div style={{ height: Math.max(barHeight - 2, 0) }} />
          <div ref={messagesEndRef} />
        </div>
      </div>
    </div>
  );
}

function ModelAvatar({ iconSvg }: { iconSvg?: string | null }) {
  if (iconSvg) {
    return (
      <div
        className="flex h-[25px] w-[25px] flex-shrink-0 items-center justify-center overflow-hidden rounded-full [&>svg]:h-[25px] [&>svg]:w-[25px]"
        // Same id-collision dodge as in ModelPanel — gradient-based icons
        // (Gemini etc) need per-render id prefixes to render side-by-side.
        dangerouslySetInnerHTML={{ __html: makeSvgIdsUnique(iconSvg) }}
      />
    );
  }

  return (
    <div className="flex h-[25px] w-[25px] flex-shrink-0 items-center justify-center rounded-full bg-black text-[10px] font-bold text-white">
      AI
    </div>
  );
}
